import format from 'string-template';

import { ActivityTypes, DistanceUnit, DURATION_TYPES } from '../../../../../Model/Activity';
import texts from './texts';

// Reps activities always add 80 seconds to a workout duration.
const STANDARD_REPS_DURATION = 80;

// Default pace for distance-based activities: 2.0 m/s (~7.2 km/h, moderate jog).
const DISTANCE_DEFAULT_PACE_METERS_PER_SECOND = 2.0;

const distanceToMeters = (value, unit) => {
  if (unit === DistanceUnit.KILOMETERS) return value * 1000;
  if (unit === DistanceUnit.MILES) return value * 1609.34;
  return value; // meters
};

const hasDistanceActivities = (activities) => activities.some((activity) => {
  if (activity.activities) {
    return hasDistanceActivities(activity.activities);
  }
  return activity.type === ActivityTypes.DISTANCE;
});

const COMPONENT_REARRANGE_SCREEN_WIDTH = 1240;

const EditorActionType = {
  CIRCUIT: 'CIRCUIT',
  ACTIVITY: 'ACTIVITY',
  REST: 'REST',
};

const AssignmentUpdateType = {
  SELECTED_ASSIGNMENT: 'SELECTED_ASSIGNMENT',
  NOT_COMPLETED_ASSIGNMENTS: 'NOT_COMPLETED_ASSIGNMENTS',
};

const assignmentsUpdateOptions = (workoutName, count = 0) => ([
  {
    value: AssignmentUpdateType.SELECTED_ASSIGNMENT,
    label: format(texts.assignmentUpdateOptionLabel[AssignmentUpdateType.SELECTED_ASSIGNMENT], { workoutName }),
  },
  {
    value: AssignmentUpdateType.NOT_COMPLETED_ASSIGNMENTS,
    label: format(texts.assignmentUpdateOptionLabel[AssignmentUpdateType.NOT_COMPLETED_ASSIGNMENTS], {
      count,
      workoutName,
    }),
  },
]);

/**
 * Estimate the duration based on the activities provided. The following chart determines how much time we need to
 * add for each activity we find:
 *  - TIMED activities: the value stored in the duration field + restTime (if it's set for the activity)
 *  - REST activities: the value stored in the duration field.
 *  - LONG_SESSION activities: the value stored in the duration field (the video's length, written by the server).
 *  - REPETITIONS: we always add 80 seconds to the duration + restTime,
 *    no matter how many reps the exercise is configured with.
 *  - CIRCUIT: We sum the contents of the circuit, and the result is multiplied by the number of rounds.
 *  - DISTANCE: convert to meters using distanceUnit, divide by default pace (2.0 m/s) + restTime.
 *
 * @param {array} activities The array with the activities data we'll use to estimate the duration.
 * @returns {number} The estimated duration of the workout.
 */
const estimateDuration = (activities) => {
  let estimatedDuration = 0;

  activities.forEach((activity) => {
    if (activity.activities) {
      const circuitDuration = estimateDuration(activity.activities);
      estimatedDuration += circuitDuration * (activity.rounds || 1);
    } else {
      if (DURATION_TYPES.has(activity.type)) {
        estimatedDuration += activity.duration;
      } else if (activity.type === ActivityTypes.DISTANCE) {
        const meters = distanceToMeters(activity.distance || 0, activity.distanceUnit);
        estimatedDuration += Math.round(meters / DISTANCE_DEFAULT_PACE_METERS_PER_SECOND);
      } else {
        estimatedDuration += STANDARD_REPS_DURATION;
      }

      if (activity.restTime) {
        estimatedDuration += activity.restTime;
      }
    }
  });

  return estimatedDuration;
};

export {
  COMPONENT_REARRANGE_SCREEN_WIDTH,
  estimateDuration,
  hasDistanceActivities,
  EditorActionType,
  AssignmentUpdateType,
  assignmentsUpdateOptions,
};
